import { useMemo, useState } from 'react';
import { IconButton } from '@lendi-ui/button';
import getColour from '@lendi-ui/commons/colours';
import ChevronLeft from '@lendi-ui/icon/ChevronLeft';
import ChevronRight from '@lendi-ui/icon/ChevronRight';

import { useDidUpdate } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { ABNDetailsSimplified } from '@funnels/types';
import { run } from '@funnels/utils';

import {
  ABN,
  Name,
  Paginator,
  PaginatorButton,
  SearchListContainer,
  SearchListHeader,
  SearchListItem,
  SelectButton,
} from './style';

const PAGE_SIZE = 5;
const PAGE_LIMIT = 2;
const MAX_RESULTS = PAGE_LIMIT * PAGE_SIZE;

export interface ABNSearchListProps {
  ABNList: ABNDetailsSimplified[];
  handleABNSelect: (abn: string) => void;
  formatABN: (abn: string | number) => string;
}

export const ABNSearchList = (props: ABNSearchListProps) => {
  const { tracking } = useAnalytics();
  const { ABNList, formatABN, handleABNSelect } = props;
  const [page, setPage] = useState(0);
  const totalResults = ABNList.length;
  const resultNumber = useMemo(() => Math.min(totalResults, MAX_RESULTS), [totalResults]);
  const totalPages = useMemo(() => Math.min(Math.ceil(totalResults / PAGE_SIZE), PAGE_LIMIT), [totalResults]);
  const ABNListInCurrentPage = useMemo(
    () => ABNList.slice(PAGE_SIZE * page, PAGE_SIZE * page + PAGE_SIZE),
    [ABNList, page]
  );

  useDidUpdate(() => {
    setPage(0);
  }, [ABNList]);

  return (
    <SearchListContainer>
      <SearchListHeader>
        {resultNumber ? `${resultNumber} results found.` : 'No results found. Try refining your search.'}
      </SearchListHeader>
      {ABNListInCurrentPage.map((ABNDetails, index) => (
        <SearchListItem key={`abn-${ABNDetails.abn}-${index}`}>
          <Name>{ABNDetails.entityName}</Name>
          <ABN>
            ABN: {formatABN(ABNDetails.abn)}; {ABNDetails.addressState} {ABNDetails.addressPostcode}
          </ABN>
          <SelectButton
            onClick={run(
              () => handleABNSelect(ABNDetails.abn.toString()),
              () => tracking?.button.link({ text: 'SELECT' })
            )}
          >
            SELECT
          </SelectButton>
        </SearchListItem>
      ))}
      {totalPages > 1 && (
        <Paginator>
          <IconButton
            aria-label="previous page"
            icon={ChevronLeft}
            isDisabled={page === 0}
            onClick={() => setPage(page - 1)}
          />
          {Array(totalPages)
            .fill(0)
            .map((_, index) => (
              <PaginatorButton
                key={`page-${index + 1}`}
                color={page === index ? getColour('interaction', 'disabled') : getColour('interaction', 'main')}
                aria-label={`${page === index ? 'Current page, ' : ''} page ${index + 1}`}
                onClick={() => setPage(index)}
                disabled={page === index}
              >
                {index + 1}
              </PaginatorButton>
            ))}
          <IconButton
            aria-label="next page"
            icon={ChevronRight}
            isDisabled={page === totalPages - 1}
            onClick={() => setPage(page + 1)}
          />
        </Paginator>
      )}
    </SearchListContainer>
  );
};
